const STORAGE_PREFIX = "lancesSeenFreightIds:";
const INITIALIZED_PREFIX = "lancesSeenFreightIdsInitialized:";

function storageKey(viewerKey) {
  return `${STORAGE_PREFIX}${String(viewerKey || "").trim()}`;
}

function initializedKey(viewerKey) {
  return `${INITIALIZED_PREFIX}${String(viewerKey || "").trim()}`;
}

function readSeenIds(viewerKey) {
  const key = String(viewerKey || "").trim();
  if (!key) return new Set();

  try {
    const raw = localStorage.getItem(storageKey(key));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(
      (Array.isArray(parsed) ? parsed : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean),
    );
  } catch (error) {
    console.warn("[lancesNewFreightsBadge] Erro ao ler fretes vistos:", error);
    return new Set();
  }
}

function writeSeenIds(viewerKey, idsSet) {
  const key = String(viewerKey || "").trim();
  if (!key) return;

  try {
    localStorage.setItem(storageKey(key), JSON.stringify(Array.from(idsSet)));
  } catch (error) {
    console.warn("[lancesNewFreightsBadge] Erro ao salvar fretes vistos:", error);
  }
}

export function hasInitializedLancesSeen(viewerKey) {
  const key = String(viewerKey || "").trim();
  if (!key) return false;
  return localStorage.getItem(initializedKey(key)) === "1";
}

export function markLancesSeenInitialized(viewerKey) {
  const key = String(viewerKey || "").trim();
  if (!key) return;
  localStorage.setItem(initializedKey(key), "1");
}

/** Marca fretes como vistos (merge). Dispara evento para atualizar o badge no menu. */
export function markLancesFreightsAsSeen(viewerKey, freightIds = []) {
  const key = String(viewerKey || "").trim();
  if (!key) return;

  const seen = readSeenIds(key);
  let changed = false;

  (freightIds || []).forEach((id) => {
    const freightId = String(id || "").trim();
    if (!freightId || seen.has(freightId)) return;
    seen.add(freightId);
    changed = true;
  });

  if (!hasInitializedLancesSeen(key)) {
    markLancesSeenInitialized(key);
    changed = true;
  }

  if (changed) {
    writeSeenIds(key, seen);
    window.dispatchEvent(
      new CustomEvent("lances-seen-freights-updated", { detail: { viewerKey: key } }),
    );
  }
}

/**
 * Na primeira execução, inicializa os IDs atuais como vistos (não mostra badge de legado).
 * Depois, conta apenas fretes ainda não vistos.
 */
export function countUnseenLancesFreights(viewerKey, freightIds = []) {
  return getUnseenLancesFreightIds(viewerKey, freightIds).length;
}

/**
 * Retorna IDs de fretes ainda não vistos.
 * Na 1ª execução, inicializa como vistos e retorna [].
 */
export function getUnseenLancesFreightIds(viewerKey, freightIds = []) {
  const key = String(viewerKey || "").trim();
  if (!key) return [];

  const ids = (freightIds || [])
    .map((id) => String(id || "").trim())
    .filter(Boolean);

  if (!hasInitializedLancesSeen(key)) {
    writeSeenIds(key, new Set(ids));
    markLancesSeenInitialized(key);
    return [];
  }

  const seen = readSeenIds(key);
  return ids.filter((id) => !seen.has(id));
}

export function getLancesSeenFreightIds(viewerKey) {
  return readSeenIds(viewerKey);
}

/** Monta texto do tooltip com nomes dos embarcadores dos fretes novos. */
export function buildLancesBadgeTooltip(shipperNames = []) {
  const names = Array.from(
    new Set(
      (shipperNames || [])
        .map((name) => String(name || "").trim())
        .filter(Boolean),
    ),
  );

  if (!names.length) return "";
  if (names.length === 1) return `Novo frete de ${names[0]}`;
  if (names.length <= 3) return `Novos fretes: ${names.join(", ")}`;
  return `Novos fretes: ${names.slice(0, 3).join(", ")} e mais ${names.length - 3}`;
}

function asNonEmptyName(value) {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || text === "—" || text === "-") return "";
  return text;
}

/**
 * Preferência: nome do pagador; depois embarcador/shipper do item da API.
 */
export function extractAvailableFreightPayerName(item) {
  if (!item || typeof item !== "object") return "";

  const clientPayment =
    item.clientPayment ?? item.client_payment ?? item.client ?? {};
  const pagadorRaw = item.pagador ?? item.freight?.pagador;
  const pagadorObj =
    pagadorRaw && typeof pagadorRaw === "object" ? pagadorRaw : null;
  const shipperObj =
    item.shipper && typeof item.shipper === "object" ? item.shipper : null;

  return (
    asNonEmptyName(item.clientPaymentName) ||
    asNonEmptyName(clientPayment?.name) ||
    asNonEmptyName(clientPayment?.nameFantasy) ||
    asNonEmptyName(clientPayment?.socialName) ||
    asNonEmptyName(clientPayment?.dataPersonal?.nameFantasy) ||
    asNonEmptyName(clientPayment?.dataPersonal?.socialName) ||
    asNonEmptyName(pagadorObj?.name) ||
    asNonEmptyName(pagadorObj?.nameFantasy) ||
    asNonEmptyName(pagadorObj?.socialName) ||
    asNonEmptyName(pagadorObj?.dataPersonal?.nameFantasy) ||
    asNonEmptyName(pagadorObj?.dataPersonal?.socialName) ||
    asNonEmptyName(item.shipperName) ||
    asNonEmptyName(shipperObj?.name) ||
    asNonEmptyName(shipperObj?.nameFantasy) ||
    asNonEmptyName(shipperObj?.socialName) ||
    asNonEmptyName(shipperObj?.dataPersonal?.nameFantasy) ||
    asNonEmptyName(shipperObj?.dataPersonal?.socialName) ||
    asNonEmptyName(typeof item.shipper === "string" ? item.shipper : "") ||
    asNonEmptyName(item.embarcador) ||
    ""
  );
}
